import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { useReviews } from '@/features/reviews/api/use-reviews'
import { ProductReviews } from '@/widgets/product-reviews'
import { Button } from '@/shared/ui/button'
import { Skeleton } from '@/shared/ui/skeleton'
import { Filter } from 'lucide-react'
import { useTelegram, useBackButton } from '@/shared/lib/telegram'

const reviewsSearchSchema = z.object({
  productId: z.string().optional(),
  productType: z.enum(['tea', 'teaware']).optional(),
  sort: z.enum(['date-desc', 'date-asc', 'rating-desc', 'rating-asc']).optional(),
  rating: z.number().int().min(1).max(5).optional(),
})

export const Route = createFileRoute('/reviews')({
  validateSearch: reviewsSearchSchema,
  component: ReviewsPage,
})

/**
 * Страница: Отзывы
 * 
 * Страница для просмотра отзывов о товарах с возможностью фильтрации и сортировки.
 * 
 * @example
 * Маршрут: /reviews
 * Маршрут с фильтром: /reviews?productId=123&productType=tea&sort=rating-desc
 */
function ReviewsPage() {
  const { productId, productType, sort, rating } = Route.useSearch()
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()

  // Нативная кнопка "Назад" в заголовке Telegram
  useBackButton(true)

  // Если указан productId и productType, показываем отзывы для товара
  const { data, isLoading, error } = useReviews({
    productId: productId || '',
    productType: (productType as 'tea' | 'teaware') || 'tea',
    sort: sort || 'date-desc',
    rating: rating,
    page: 1,
    limit: 50,
  })

  const reviews = data?.data ?? []

  // Если нет productId, показываем сообщение
  if (!productId || !productType) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold mb-4">Отзывы</h1>
          <p className="text-muted-foreground mb-6">
            Выберите товар, чтобы посмотреть отзывы
          </p>
          <Button
            variant="primary"
            onClick={() => {
              hapticFeedback?.impactOccurred('light')
              navigate({ to: '/catalog/$section', params: { section: 'tea' } })
            }}
          >
            Перейти в каталог
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Заголовок */}
      <div>
        <h1 className="text-2xl font-bold text-ink dark:text-white">Отзывы</h1>
        {productId && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Товар ID: {productId}
          </p>
        )}
      </div>

      {/* Фильтры */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-gray-500" />
        <span className="text-sm text-gray-600">Сортировка:</span>
        {['date-desc', 'date-asc', 'rating-desc', 'rating-asc'].map((sortOption) => (
          <Button
            key={sortOption}
            variant={sort === sortOption ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => {
              hapticFeedback?.impactOccurred('light')
              navigate({
                to: '/reviews',
                search: { productId, productType, sort: sortOption as any, rating },
              })
            }}
          >
            {sortOption === 'date-desc' && 'Новые'}
            {sortOption === 'date-asc' && 'Старые'}
            {sortOption === 'rating-desc' && 'Высокий рейтинг'}
            {sortOption === 'rating-asc' && 'Низкий рейтинг'}
          </Button>
        ))}
      </div>

      {/* Отзывы */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-xl" />
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-error mb-4">Ошибка загрузки отзывов</p>
          <Button
            variant="secondary"
            onClick={() => window.location.reload()}
          >
            Попробовать снова
          </Button>
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-2">Отзывов пока нет</p>
          <p className="text-sm text-gray-400">
            Будьте первым, кто оставит отзыв!
          </p>
        </div>
      ) : (
        <ProductReviews
          productId={productId}
          productType={productType as 'tea' | 'teaware'}
          maxReviews={50}
          showSummary={true}
        />
      )}
    </div>
  )
}

